/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { editCaseCreation, editOpeningCaseFile } from "services/caseServices";
import { CaseCreation, OpeniningCaseFile } from "types";
import { BASE_URL } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  file: OpeniningCaseFile;
  caseData: CaseCreation;
  assignedProsecutor: string;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const ReAssignedProsecutorToCourt: React.FC<Props> = ({
  file,
  caseData,
  assignedProsecutor,
  setRefetch,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = React.useState(false);
  const [prosecutors, setProsecutors] = React.useState<
    {
      user: {
        id: string;
        first_name: string;
        last_name: string;
      };
    }[]
  >([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedName, setSelectedName] = React.useState<string | null>(null);

  const fetchData = (adminId: string | number, orgId: string | number) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
      })
      .catch((error) => {
        console.error("Error fetching prosecutors:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData]);

  useEffect(() => {
    if (file?.assign_prosecutor && prosecutors) {
      setSelectedId(file?.userid_assign_prosecutor ?? null);
      setSelectedName(file?.assign_prosecutor);
    }
  }, [file, prosecutors]);

  useEffect(() => {
    if (assignedProsecutor && prosecutors) {
      setSelectedId(assignedProsecutor);
    }
  }, [assignedProsecutor, prosecutors]);

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="mb-3 font-[Poppins] font-semibold text-base border-b border-[#D4D4D4] pb-2">
        Re-assigned to
      </h2>

      <div className="flex flex-col gap-2 ">
        <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
          Currently Assigned Prosecutor
          <span className="text-red-500">{file?.assign_prosecutor}</span>
        </label>

        <select
          className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
          name="reception_method"
          value={selectedId || ""}
          onChange={(e) => {
            const selectedProsecutor = prosecutors.find(
              (p) => p?.user?.id?.toString() === e.target.value,
            );
            if (selectedProsecutor) {
              setSelectedId(
                selectedProsecutor.user?.id !== undefined &&
                  selectedProsecutor.user?.id !== null
                  ? String(selectedProsecutor.user?.id)
                  : "",
              );
              setSelectedName(
                selectedProsecutor.user?.first_name +
                  " " +
                  selectedProsecutor.user?.last_name,
              );
            }
          }}
          required
        >
          <option value={""}>
            {loading ? "Loading ...." : "Select Prosecutor"}
          </option>
          {Array.isArray(prosecutors) &&
            prosecutors.map((p, index) => (
              <option key={index} value={p.user?.id || ""}>
                {p?.user?.first_name + " " + p?.user?.last_name}
              </option>
            ))}
        </select>
      </div>

      <input
        type="button"
        className={`border bg-[#212121] px-[76px] py-2  w-full mt-3
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]`}
        value={"Re-assign Prosecutor"}
        disabled={loading || !prosecutors || prosecutors.length === 0}
        onClick={async () => {
          if (!selectedId) {
            showTooltip(t("no_prosecutors_available_to_assign"), "error");
            return;
          }

          setLoading(true);
          editOpeningCaseFile({
            // ...file,
            id: file?.id,
            assign_prosecutor: selectedName,
            userid_assign_prosecutor: selectedId,
            // status: "assigned_to_prosecutor",
          })
            .then(() => {
              if (caseData) {
                // Re-assigning a prosecutor puts the case back on the court
                // hearing and resolves any court-update request.
                editCaseCreation({
                  id: caseData?.id,
                  status: "sent_to_court",
                  status_filter: "on_court_hearing",
                  is_case_updated_by_court: false,
                });
              }
              showTooltip(t("successfully_submitted"), "success");
              setRefetch(true);
            })
            .catch((error) => {
              showTooltip(
                error?.response?.data?.detail || errorHandlerOnForm(error),
                "error",
              );
            })
            .finally(() => setLoading(false));
        }}
      />
    </div>
  );
};

export default ReAssignedProsecutorToCourt;
